import { isString } from 'lodash';

import { INovaEntity } from './base';
import { formatPhoneNumber, normalizeLanguageTag } from './js-helpers';
import type { Maybe, Nullable } from './globals';
import {
  CANONICAL_STOP_TRANSLATE_KEYWORDS,
  type DriverGreetingCometLocale,
  DRIVER_GREETING_COMET_LOCALES
} from './message-thread-stop-translate';
import { haveSameBaseLanguage, languageTagBase } from './message-thread-translation';

export interface IMessageThread extends INovaEntity {
  orgId: string;
  assetVisitId: string;
  messages?: IMessageThreadMessage[];
  expiresAt: Date;
  usesWhatsApp?: boolean;
  translationEnabled?: boolean;
  driverTranslationEnabled?: boolean;
  firstTranslatedOutboundSent?: boolean;
  hasDriverReplied?: boolean;
}

export enum IMessageStatus {
  DELIVERED = 'delivered',
  FAILED = 'failed',
  SENT = 'sent',
  RECEIVED = 'received'
}

export interface IMessageThreadMessageFile {
  name: string;
  mimeType: AllowedMessageThreadMediaMimeTypes;
  url: string;
  size: number;
}

export enum AllowedMessageThreadMediaMimeTypes {
  JPG = 'image/jpeg',
  PNG = 'image/png',
  GIF = 'image/gif',
  HEIC = 'image/heic',
  PDF = 'application/pdf',
  DOC = 'application/msword',
  DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  CSV = 'text/csv',
  TSV = 'text/tab-separated-values'
}

export enum MessageSourceType {
  DRIVER = 'driver',
  WAREHOUSE = 'warehouse',
  SYSTEM = 'system'
}

export enum MessageTranslationStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
  SKIPPED = 'skipped',
  FAILED = 'failed'
}

export interface IMessageThreadMessageTranslation extends INovaEntity {
  orgId: string;
  messageThreadMessageId: string;
  targetLanguageTag: string;
  content: string;
  confidence: Nullable<number>;
  provider: Nullable<string>;
}

export interface IMessageThreadMessageTranslationResponse {
  targetLanguageTag: string;
  content: string;
  confidence?: Nullable<number>;
  provider?: Nullable<string>;
}

export const MessageThreadMessageTranslationNotes = {
  ENABLED: 'Translation is enabled.',
  UNAVAILABLE: 'Translation unavailable.'
} as const;

export interface IMessageThreadMessage {
  providerMessageId: string;
  orgId?: string;
  messageThreadId: string;
  timestamp?: Date;
  content?: Maybe<string>;
  files?: IMessageThreadMessageFile[];
  toNumber: string;
  fromNumber: string;
  status: IMessageStatus;
  createdBy?: Nullable<string>;
  sourceType?: MessageSourceType;
  translationStatus?: Nullable<MessageTranslationStatus>;
  sourceLanguageTag?: Nullable<string>;
  sourceLanguageConfidence?: Nullable<number>;
}

export interface IMessageThreadEvent {
  orgId: string;
  messageThreadId: string;
  content: string;
}

export function isMessageThreadExpired(messageThread: IMessageThread): boolean {
  const expiresAtUTC = new Date(messageThread.expiresAt).getTime();
  const nowUTC = new Date().getTime();
  return nowUTC > expiresAtUTC;
}

enum SubscriptionKeyword {
  START = 'start',
  RECIBIR = 'recibir',
  RECEBER = 'receber'
}

const UnsubscriptionKeyword = {
  STOP: 'stop',
  ALTO: 'alto',
  PARAR: 'parar',
  ARRETER: 'arrêter',
  EIQAF: 'إيقاف',
  ZATRZYMAJ: 'zatrzymaj',
  СТОП: 'стоп',
  ROKEEN: 'रोकें'
} as const;
type UnsubscriptionKeyword = (typeof UnsubscriptionKeyword)[keyof typeof UnsubscriptionKeyword];

export function isDriverSubscribing(content: string): boolean {
  return Object.values(SubscriptionKeyword).includes(content?.toLowerCase() as SubscriptionKeyword);
}

export function isDriverUnsubscribing(content: string): boolean {
  return (
    isString(content) &&
    Object.values(UnsubscriptionKeyword).includes(
      content.trim().toLowerCase() as UnsubscriptionKeyword
    )
  );
}

/**
 * Sanitizes a Twilio/WhatsApp phone number by removing the `whatsapp:` prefix
 * and any other non-phone characters.
 */
export function sanitizeTwilioWhatsAppPhoneNumber(phoneNumber: string): string {
  // Only `+` and `0-9` characters remain
  return phoneNumber.replace(/[^0-9+]/gu, '');
}

/** ISO 3166 alpha-3 codes (Nova filtered list) where driver INFO greetings use Spanish. */
const spanishDriverInfoGreetingCountries = new Set<string>([
  'ARG',
  'BOL',
  'CHL',
  'COL',
  'CRI',
  'CUB',
  'DOM',
  'ECU',
  'ESP',
  'GTM',
  'HND',
  'MEX',
  'NIC',
  'PAN',
  'PER',
  'PRI',
  'PRY',
  'SLV',
  'URY',
  'VEN'
]);

/** True when {@link SystemMessages.INFO} driver greetings use Spanish for this warehouse country. */
export function isSpanishDriverInfoGreetingCountry(country?: string): boolean {
  return Boolean(country && spanishDriverInfoGreetingCountries.has(country));
}

const warehouseCountryLanguageTags: Record<string, string> = {
  USA: 'en',
  CAN: 'en-CA',
  BRA: 'pt-BR'
};

/** Maps warehouse ISO alpha-3 country to a BCP-47 tag. */
export function warehouseLanguageTagFromCountry(country?: string | null): string {
  if (!country) {
    return 'en';
  }
  if (warehouseCountryLanguageTags[country]) {
    return warehouseCountryLanguageTags[country];
  }
  if (isSpanishDriverInfoGreetingCountry(country)) {
    return 'es-MX';
  }
  return 'en';
}

type LegacyDriverInfoGreetingLocale = 'en' | 'es' | 'pt';

export type DriverGreetingChannel = 'sms' | 'whatsapp';

export type ResolveDriverGreetingInput = {
  warehouseName: string;
  country?: string;
  driverLanguageTag?: Maybe<string>;
  translationEnabled: boolean;
  channel: DriverGreetingChannel;
};

const cometLocaleBases = new Set(
  Object.values(DRIVER_GREETING_COMET_LOCALES).map(tag => languageTagBase(tag))
);

/**
 * Maps a persisted / Comet `driverLanguageTag` onto a supported greeting language key, or null.
 * Always resolves by primary language (`es-MX` / `es` → `es`, `pt-BR` / `pt` → `pt`).
 */
export function resolveSupportedDriverGreetingLocale(
  driverLanguageTag?: Maybe<string>
): Maybe<DriverGreetingCometLocale> {
  if (!isString(driverLanguageTag) || !driverLanguageTag.trim()) {
    return null;
  }
  const normalized = normalizeLanguageTag(driverLanguageTag.trim());
  if (!isString(normalized)) {
    return null;
  }
  const baseLanguage = languageTagBase(normalized);
  if (!cometLocaleBases.has(baseLanguage)) {
    return null;
  }
  return baseLanguage as DriverGreetingCometLocale;
}

/**
 * Env-var suffix for localized WhatsApp greeting Content SIDs
 * (e.g. `es` → `ES_WITH_STOP_TRANSLATION` → `DRIVER_GREETING_TEMPLATE_ID_ES_WITH_STOP_TRANSLATION`).
 */
export function driverGreetingLocalizedTemplateEnvSuffix(
  locale: DriverGreetingCometLocale
): string {
  return `${locale.toUpperCase()}_WITH_STOP_TRANSLATION`;
}

function driverInfoGreetingLocale(country?: string): LegacyDriverInfoGreetingLocale {
  if (country === 'BRA') {
    return 'pt';
  }
  if (isSpanishDriverInfoGreetingCountry(country)) {
    return 'es';
  }
  return 'en';
}

function legacyDriverInfoGreetings(
  warehouseName: string,
  locale: LegacyDriverInfoGreetingLocale
): { greeting: string; interactiveGreeting: string } {
  switch (locale) {
    case 'pt':
      return {
        greeting: `Obrigado por seu check-in em ${warehouseName}. Instruções serão enviadas para este número. Responda PARAR caso não queira receber mais mensagens.`,
        interactiveGreeting: `Obrigado por seu check-in em ${warehouseName}. Responda OK para receber instruções do armazém. Responda PARAR caso não queira receber mais mensagens.`
      };
    case 'es':
      return {
        greeting: `Gracias por registrarte en ${warehouseName}. Las instrucciones serán enviadas a este número. Responde ALTO si no deseas recibir más mensajes.`,
        interactiveGreeting: `Gracias por registrarte en ${warehouseName}. Responde OK para recibir las instrucciones del almacén. Responde STOP si no deseas recibir más mensajes.`
      };
    default:
      return {
        greeting: `Thanks for checking in at ${warehouseName}. Instructions will follow to this phone number. Reply STOP to unsubscribe.`,
        interactiveGreeting: `Thanks for checking in at ${warehouseName}. Reply OK to receive instructions from the warehouse. Reply STOP to unsubscribe.`
      };
  }
}

/** Complete localized greetings (stop-translation sentence baked into the body). */
function localizedDriverInfoGreetings(
  warehouseName: string,
  locale: DriverGreetingCometLocale
): { greeting: string; interactiveGreeting: string } {
  const stopTranslate = CANONICAL_STOP_TRANSLATE_KEYWORDS[locale];

  switch (locale) {
    case 'es':
      return {
        greeting: `Gracias por registrarte en ${warehouseName}. Las instrucciones serán enviadas a este número. Responde ALTO si no deseas recibir más mensajes. Responde DETENER TRADUCCIÓN para desactivar la traducción automática.`,
        interactiveGreeting: `Gracias por registrarte en ${warehouseName}. Responde OK para recibir las instrucciones del almacén. Responde STOP si no deseas recibir más mensajes. Responde DETENER TRADUCCIÓN para desactivar la traducción automática.`
      };
    case 'pt':
      return {
        greeting: `Obrigado por seu check-in em ${warehouseName}. Instruções serão enviadas para este número. Responda PARAR caso não queira receber mais mensagens. Responda PARAR TRADUÇÃO para desativar a tradução automática.`,
        interactiveGreeting: `Obrigado por seu check-in em ${warehouseName}. Responda OK para receber instruções do armazém. Responda PARAR caso não queira receber mais mensagens. Responda PARAR TRADUÇÃO para desativar a tradução automática.`
      };
    case 'fr':
      return {
        greeting: `Merci pour votre enregistrement à ${warehouseName}. Les instructions seront envoyées à ce numéro. Répondez STOP si vous ne souhaitez plus recevoir de messages. Répondez ARRÊTER TRADUCTION pour désactiver la traduction automatique.`,
        interactiveGreeting: `Merci pour votre enregistrement à ${warehouseName}. Répondez OK pour recevoir les instructions de l'entrepôt. Répondez STOP si vous ne souhaitez plus recevoir de messages. Répondez ARRÊTER TRADUCTION pour désactiver la traduction automatique.`
      };
    case 'ar':
      return {
        greeting: `شكراً لتسجيلك في ${warehouseName}. سيتم إرسال التعليمات إلى هذا الرقم. رد بـ STOP إذا كنت لا تريد تلقي المزيد من الرسائل. رد بـ إيقاف الترجمة لإيقاف الترجمة التلقائية.`,
        interactiveGreeting: `شكراً لتسجيلك في ${warehouseName}. رد بـ OK لتلقي تعليمات المستودع. رد بـ STOP إذا كنت لا تريد تلقي المزيد من الرسائل. رد بـ إيقاف الترجمة لإيقاف الترجمة التلقائية.`
      };
    case 'pl':
      return {
        greeting: `Dziękujemy za zameldowanie w ${warehouseName}. Instrukcje zostaną wysłane na ten numer. Odpowiedz STOP, jeśli nie chcesz otrzymywać więcej wiadomości. Odpowiedz ZATRZYMAJ TŁUMACZENIE, aby wyłączyć automatyczne tłumaczenie.`,
        interactiveGreeting: `Dziękujemy za zameldowanie w ${warehouseName}. Odpowiedz OK, aby otrzymać instrukcje z magazynu. Odpowiedz STOP, jeśli nie chcesz otrzymywać więcej wiadomości. Odpowiedz ZATRZYMAJ TŁUMACZENIE, aby wyłączyć automatyczne tłumaczenie.`
      };
    case 'ru':
      return {
        greeting: `Спасибо за регистрацию в ${warehouseName}. Инструкции будут отправлены на этот номер. Ответьте STOP, если не хотите получать сообщения. Ответьте СТОП ПЕРЕВОД, чтобы отключить автоматический перевод.`,
        interactiveGreeting: `Спасибо за регистрацию в ${warehouseName}. Ответьте OK, чтобы получить инструкции склада. Ответьте STOP, если не хотите получать сообщения. Ответьте СТОП ПЕРЕВОД, чтобы отключить автоматический перевод.`
      };
    case 'uk':
      return {
        greeting: `Дякуємо за реєстрацію в ${warehouseName}. Інструкції буде надіслано на цей номер. Відповідайте STOP, якщо не хочете отримувати повідомлення. Відповідайте СТОП ПЕРЕКЛАД, щоб вимкнути автоматичний переклад.`,
        interactiveGreeting: `Дякуємо за реєстрацію в ${warehouseName}. Відповідайте OK, щоб отримати інструкції складу. Відповідайте STOP, якщо не хочете отримувати повідомлення. Відповідайте СТОП ПЕРЕКЛАД, щоб вимкнути автоматичний переклад.`
      };
    case 'hi':
      return {
        greeting: `${warehouseName} में चेक-इन करने के लिए धन्यवाद। निर्देश इस नंबर पर भेजे जाएंगे। यदि आप और संदेश नहीं चाहते हैं तो STOP का जवाब दें। स्वचालित अनुवाद बंद करने के लिए अनुवाद बंद का जवाब दें।`,
        interactiveGreeting: `नमस्ते! ${warehouseName} पर चेक-इन करने के लिए धन्यवाद। गोदाम से निर्देश पाने के लिए OK भेजें। संदेश बंद करने के लिए STOP भेजें। स्वचालित अनुवाद रोकने के लिए "अनुवाद बंद" भेजें।`
      };
    case 'en':
    default:
      return {
        greeting: `Thanks for checking in at ${warehouseName}. Instructions will follow to this phone number. Reply STOP to unsubscribe. Reply STOP TRANSLATION to turn off automatic translation.`,
        interactiveGreeting: `Thanks for checking in at ${warehouseName}. Reply OK to receive instructions from the warehouse. Reply STOP to unsubscribe. Reply STOP TRANSLATION to turn off automatic translation.`
      };
  }
}

/**
 * Resolves the check-in greeting body for SMS or WhatsApp.
 *
 * Localized path (translation flag on + supported driver tag + different base from warehouse):
 * complete Comet-locale message with stop-translation instructions in the body.
 * Otherwise: legacy country → en/es/pt greeting without stop-translation instructions.
 */
export function resolveDriverGreeting(input: ResolveDriverGreetingInput): string {
  const legacy = legacyDriverInfoGreetings(
    input.warehouseName,
    driverInfoGreetingLocale(input.country)
  );
  const legacyContent = input.channel === 'whatsapp' ? legacy.interactiveGreeting : legacy.greeting;

  if (!input.translationEnabled) {
    return legacyContent;
  }

  const driverLocale = resolveSupportedDriverGreetingLocale(input.driverLanguageTag);
  if (!driverLocale) {
    return legacyContent;
  }

  const warehouseLanguageTag = warehouseLanguageTagFromCountry(input.country);
  if (haveSameBaseLanguage(driverLocale, warehouseLanguageTag)) {
    return legacyContent;
  }

  const localized = localizedDriverInfoGreetings(input.warehouseName, driverLocale);
  return input.channel === 'whatsapp' ? localized.interactiveGreeting : localized.greeting;
}

/**
 * True when the localized (stop-translation) greeting path should be used for WhatsApp
 * template selection / persisted content.
 */
export function shouldUseLocalizedDriverGreeting(input: {
  translationEnabled: boolean;
  driverLanguageTag?: Maybe<string>;
  country?: string;
}): boolean {
  if (!input.translationEnabled) {
    return false;
  }
  const driverLocale = resolveSupportedDriverGreetingLocale(input.driverLanguageTag);
  if (!driverLocale) {
    return false;
  }
  return !haveSameBaseLanguage(driverLocale, warehouseLanguageTagFromCountry(input.country));
}

function driverReopenMessage(
  warehouseName: string,
  locale: LegacyDriverInfoGreetingLocale
): string {
  switch (locale) {
    case 'pt':
      return `Esta conversa foi encerrada automaticamente após 24 horas. O armazém ${warehouseName} está tentando se comunicar com você. Responda OK para receber a mensagem.`;
    case 'es':
      return `Este chat se cerró automáticamente después de 24 horas. El almacén ${warehouseName} está intentando comunicarse contigo. Responde OK para recibir el mensaje.`;
    default:
      return `This thread was automatically closed after 24 hours. The warehouse ${warehouseName} is trying to communicate with you. Reply OK to receive message.`;
  }
}

export const SystemMessages = {
  ERROR: {
    DEFAULT: () =>
      'Automated Response: An error occurred while processing your message. Please try again later.',
    INVALID_FILE_TYPE: (fileType?: string) =>
      `Automated Response: Sorry, your message was not delivered. ${
        fileType
          ? `The file type ${fileType} is not supported.`
          : 'This file type is not supported.'
      }`,
    THREAD_NOT_FOUND: (warehouseName: string) =>
      `Automated Response: Sorry, your message was not delivered. There are no active conversations with ${warehouseName}.`,
    THREAD_CLOSED: (warehouseName: string) =>
      `Automated Response: Sorry, your message was not delivered. Conversation with ${warehouseName} has been closed.`
  },
  EVENT: {
    DRIVER_SUBSCRIBED: () => 'The driver has opted to start receiving messages',
    DRIVER_UNSUBSCRIBED: () => 'The driver has opted to stop receiving messages',
    DRIVER_STOPPED_TRANSLATION: () => 'The driver has opted to stop translation',
    WAREHOUSE_STOPPED_TRANSLATION: () => 'The warehouse has opted to stop translation',
    WAREHOUSE_RESUMED_TRANSLATION: () => 'The warehouse has opted to resume translation',
    PHONE_CHANGED: (phone: string) => `Driver's phone changed to ${formatPhoneNumber(phone)}`,
    THREAD_OPEN: () => 'Chat resumed by the warehouse',
    THREAD_CLOSED: () => 'Chat ended by the warehouse',
    THREAD_REOPENED: () => 'Chat reopened by the warehouse',
    THREAD_CLOSED_BY_ANOTHER_CHECKIN: (warehouseName: string) =>
      `Chat at ${warehouseName} ended due to another check-in`
  },
  INFO: {
    /** Legacy country-based SMS greeting (no stop-translation instructions). */
    DRIVER_GREETING: (warehouseName: string, country?: string) =>
      legacyDriverInfoGreetings(warehouseName, driverInfoGreetingLocale(country)).greeting,
    /** Legacy country-based WhatsApp interactive greeting (no stop-translation instructions). */
    DRIVER_INTERACTIVE_GREETING: (warehouseName: string, country?: string) =>
      legacyDriverInfoGreetings(warehouseName, driverInfoGreetingLocale(country))
        .interactiveGreeting,
    DRIVER_REOPEN: (warehouseName: string, country?: string) =>
      driverReopenMessage(warehouseName, driverInfoGreetingLocale(country)),
    APPOINTMENT_IN_PROGRESS: (warehouseName: string, dockName: string) =>
      `Your appointment at ${warehouseName} is now in progress. You have been assigned to dock ${dockName}.`,
    APPOINTMENT_COMPLETED: (warehouseName: string) =>
      `Your appointment at ${warehouseName} has been completed.`,
    PICKUP_ASSET_LOCATION: (
      assetType: string,
      assetCode: string,
      spotAreaName: string,
      status: string
    ) =>
      `Your pickup ${assetType} ${assetCode} is located at ${spotAreaName}. Current status: ${status}.`
  }
};
